// router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import store from '../store'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { requiresAuth: true }
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import(/* webpackChunkName: "analytics" */ '../views/AnalyticsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/activity-logs',
    name: 'activity-logs',
    component: () => import(/* webpackChunkName: "analytics" */ '../views/ActivityLogsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  // Existing routes
  {
    path: '/review-candidates',
    name: 'review-candidates',
    component: () => import(/* webpackChunkName: "candidates" */ '../views/ReviewCandidatesView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'client'] }
  },
  {
    path: '/user-management',
    name: 'user-management',
    component: () => import(/* webpackChunkName: "admin" */ '../views/UserManagementView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/talent/:utm',
    name: 'talent-form-utm',
    component: () => import(/* webpackChunkName: "public-forms" */ '../views/TalentFormView.vue')
    // No auth meta - publicly accessible
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "auth" */ '../views/LoginView.vue'),
    meta: { guestOnly: true }
  },
  // {
  //   path: '/register',
  //   name: 'register',
  //   component: () => import(/* webpackChunkName: "auth" */ '../views/RegisterView.vue'),
  //   meta: { guestOnly: true }
  // },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import(/* webpackChunkName: "auth" */ '../views/ResetPasswordView.vue'),
    meta: { guestOnly: true }
  },

  // New public routes for form submissions
  {
    path: '/request',
    name: 'request-form',
    component: () => import(/* webpackChunkName: "public-forms" */ '../views/RequestFormView.vue')
    // No auth meta - publicly accessible
  },
  {
    path: '/talent',
    name: 'talent-form',
    component: () => import(/* webpackChunkName: "public-forms" */ '../views/TalentFormView.vue')
    // No auth meta - publicly accessible
  },

  // New admin routes for viewing submissions
  {
    path: '/requests',
    name: 'requests-list',
    component: () => import(/* webpackChunkName: "admin-requests" */ '../views/RequestsListView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/requests/:id',
    name: 'request-details',
    component: () => import(/* webpackChunkName: "admin-requests" */ '../views/RequestDetailsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/talents',
    name: 'talents-list',
    component: () => import(/* webpackChunkName: "admin-talents" */ '../views/TalentsListView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/talents/:id',
    name: 'talent-details',
    component: () => import(/* webpackChunkName: "admin-talents" */ '../views/TalentDetailsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import(/* webpackChunkName: "profile" */ '../views/ProfileView.vue'),
    meta: { requiresAuth: true } // Only accessible to authenticated users
  },

  // New routes for candidate compare feature
  // {
  //   path: '/candidate-compare',
  //   name: 'candidate-compare-list',
  //   component: () => import(/* webpackChunkName: "candidate-compare" */ '../views/CandidateCompareListView.vue'),
  //   meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  // },
  {
    path: '/candidate-compare/new',
    name: 'candidate-compare-new',
    component: () => import(/* webpackChunkName: "candidate-compare" */ '../views/CandidateCompareEditView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/candidate-compare/edit/:id',
    name: 'candidate-compare-edit',
    component: () => import(/* webpackChunkName: "candidate-compare" */ '../views/CandidateCompareEditView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'superadmin'] }
  },
  {
    path: '/public/candidate-compare/:id',
    name: 'candidate-compare-public',
    component: () => import(/* webpackChunkName: "candidate-compare-public" */ '../views/CandidateComparePublicView.vue')
    // No auth meta - publicly accessible
  },
  {
    path: '/public/review-candidates/:id',
    name: 'review-candidates-detail',
    component: () => import(/* webpackChunkName: "candidates" */ '../views/ReviewCandidatesView.vue'),
    // No auth meta - publicly accessible, just like the public candidate-compare route
  },
  {
    path: '/public/requests/:id',
    name: 'public-request-details',
    component: () => import(/* webpackChunkName: "public-requests" */ '../views/RequestDetailsView.vue')
    // No auth meta - publicly accessible, just like the public candidate-compare route
  },
  {
    path: '/video-record/:token',
    name: 'video-recording',
    component: () => import(/* webpackChunkName: "video-recording" */ '../views/VideoRecordingView.vue'),
    meta: { hideNavigation: true }  // Add this meta property

    // No auth required - public access with token
  },
  {
    path: '/share-candidate/:id',
    name: 'share-candidate',
    component: () => import(/* webpackChunkName: "share-candidate" */ '../views/ShareCandidateView.vue'),
    meta: { hideNavigation: true }  // Add this meta property
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Navigation guard
router.beforeEach((to, from, next) => {
  // Check if store getters exist
  let isAuthenticated = false;
  let userRole = null;

  try {
    isAuthenticated = store.getters['auth/isAuthenticated'];
    userRole = store.getters['auth/userRole'];
  } catch (error) {
    console.error('Error accessing auth store getters:', error);
    // Continue with defaults if getters don't exist
  }

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      next({ name: 'login', query: { redirect: to.fullPath } })
    } else {
      // Check for role restrictions
      const requiredRoles = to.matched.find(record => record.meta.roles)?.meta.roles

      if (requiredRoles && !requiredRoles.includes(userRole)) {
        // Redirect to home if user doesn't have required role
        next({ name: 'home' })
      } else {
        next()
      }
    }
  } else if (to.matched.some(record => record.meta.guestOnly) && isAuthenticated) {
    // Redirect to home if user is authenticated and tries to access guest-only pages
    next({ name: 'home' })
  } else {
    next()
  }
})

export default router
